import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button, { Color } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { RivalryStatus } from '@sorare/core/src/components/rivals/RivalryStatus';
import { RivalsAvatar } from '@sorare/core/src/components/rivals/RivalsAvatar';
import FollowButton from '@sorare/core/src/components/user/FollowButton';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { glossary, rivals } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RivalsProfile_footballRivalsManager } from './__generated__/index.graphql';
import pattern from './assets/pattern.svg';

const Root = styled(Vertical)`
  position: relative;
  isolation: isolate;
  gap: var(--double-unit);
  padding: var(--triple-unit) var(--double-unit);

  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    padding: 0 var(--triple-unit);
    border-radius: var(--double-unit);
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  &.background {
    background: center / cover url(${pattern});
  }
`;
const EmojiWrapper = styled.div`
  @media ${desktopAndAbove} {
    position: absolute;
    top: var(--double-unit);
    right: var(--double-unit);
  }
`;
const SpaceBetweenHorizontal = styled(Horizontal)`
  justify-content: space-between;
`;
const ButtonsWrapper = styled(Horizontal)`
  width: 100%;
  @media ${desktopAndAbove} {
    width: 70%;
    flex-direction: column;
  }
`;

type Props = {
  rivalsManager: RivalsProfile_footballRivalsManager;
  onChallenge?: () => void;
  withBackground?: boolean;
  children: ReactNode;
};
export const RivalsProfile = ({
  rivalsManager,
  onChallenge,
  withBackground = true,
  children,
}: Props) => {
  return (
    <Root className={classNames({ background: withBackground })}>
      <RivalsAvatar size={13} manager={rivalsManager} hideStatus />
      <SpaceBetweenHorizontal>
        <MyClubLink user={rivalsManager.user}>
          <HeadlineXL as="h2">
            <Ellipsis>{rivalsManager.user.nickname}</Ellipsis>
          </HeadlineXL>
        </MyClubLink>
        {rivalsManager.myRivalry?.status && (
          <EmojiWrapper>
            <RivalryStatus status={rivalsManager.myRivalry?.status} />
          </EmojiWrapper>
        )}
      </SpaceBetweenHorizontal>
      {children}
      <ButtonsWrapper>
        <FollowButton
          size="large"
          subscribable={{ __typename: 'User', slug: rivalsManager.user.slug }}
          initialSubscription={rivalsManager.user.followed}
          renderButton={(buttonProps, subscription, hovered) => {
            let followButtonColor: Color = 'secondary';
            if (subscription && hovered) {
              followButtonColor = 'red';
            } else if (subscription) {
              followButtonColor = 'tertiary';
            }

            return (
              <Button
                color={followButtonColor}
                size="medium"
                fullWidth
                {...buttonProps}
              >
                {subscription ? (
                  <Horizontal>
                    <FontAwesomeIcon icon={faCheck} />
                    <FormattedMessage
                      {...(hovered ? glossary.unfollow : glossary.following)}
                    />
                  </Horizontal>
                ) : (
                  <FormattedMessage {...glossary.follow} />
                )}
              </Button>
            );
          }}
        />
        {onChallenge && (
          <Button onClick={onChallenge} color="primary" fullWidth size="medium">
            <FormattedMessage {...rivals.challenge} />
          </Button>
        )}
      </ButtonsWrapper>
    </Root>
  );
};

RivalsProfile.fragments = {
  footballRivalsManager: gql`
    fragment RivalsProfile_footballRivalsManager on FootballRivalsManager {
      id
      user {
        id
        slug
        nickname
        followed {
          slug
        }
        ...MyClubLink_publicUserInfoInterface
      }
      myRivalry {
        id
        pastOutcomesPreview(first: 5)
        winRatio
        status {
          ...RivalryStatus_rivalryStatus
        }
      }
      ...RivalsAvatar_footballRivalsManager
    }
    ${RivalryStatus.fragments.rivalryStatus}
    ${MyClubLink.fragments.user}
    ${RivalsAvatar.fragments.footballRivalsManager}
  ` as TypedDocumentNode<RivalsProfile_footballRivalsManager>,
};
